import { service } from '@ember/service';
import Component from '@glimmer/component';
import { registerDestructor } from '@ember/destroyable';
import { debounce, cancel, type Timer } from '@ember/runloop';
import StickyLayout from 'tio-ui/components/sticky-layout';
import { Section } from 'tio-ui/components/layout';
import TooltipButton from 'tio-ui/components/tooltip-button';
import Button from 'tio-ui/components/button';
import Drawer from 'tio-ui/components/drawer';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import stickySidebarAdjustment from 'tio-ui/modifiers/sticky-sidebar-adjustment';
import { AdvancedProgramFilters } from './programs-filters.gts';
import { DatasetFilters } from '../dataset-filters.gts';
import ProgramsTable from './programs-table.gts';
import ProgramsAggregateTable from './programs-aggregate-table.gts';
import SavedReportsDrawer from '../saved-reports-drawer.gts';
import PublishReportDialog from '../publish-report-dialog.gts';
import ReportStatusBadge from '../report-status-badge.gts';
import { updateReportRequest } from '../../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import ChartView from '../chart-view.gts';
import ProgramsDataProvider from './programs-data-provider.gts';
import { tracked } from '@glimmer/tracking';
import { gt } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import { on } from '@ember/modifier';
import { fn, hash } from '@ember/helper';
import {
  PhFunnel,
  PhFloppyDisk,
  PhTrayArrowDown,
  PhCloudArrowUp,
  PhCloudX,
} from 'ember-phosphor-icons';
import ReSelect from 'tio-ui/components/select';
import { type FieldOption } from '../../utils/opensearch-field-mapper';
import { OpenSearchFieldMapper } from '../../utils/opensearch-field-mapper';
import { ChartState } from '../../utils/chart-state';
import {
  getFieldGroup as sharedGetFieldGroup,
  getDisplayGroup as sharedGetDisplayGroup,
  stripTypeSuffixes,
} from '../../utils/label-generation';
import {
  applyTemplateAndCustomFieldLabels,
  applyPerFieldTemplateLabels,
} from '../../utils/field-label-resolver';
import {
  groupFieldOptions,
  pruneSelectedColumnsByResolvedSource,
  filterFieldsByResolvedSource,
} from '../../utils/column-utils';
import ColumnSelector from '../column-selector';
import { deriveFilterColumnDependencies } from '../../utils/filter-registry';
import { applyFallbackEnumValues } from '../../utils/dataset-filter-enums';
import { and } from 'tio-ui/utilities';
import { getDefaultColumns, getModelTypeFromDataset } from '../../utils/dataset-utils';
import { AGGREGATION_COLUMNS, DEFAULT_COLUMNS } from '../../utils/tas-column-config';
import type { NotificationsService } from 'tio-ui/components/notifications';

import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { ReportsExtension as ReportModel } from 'tio-data/data/resources/reports.ext';
import type { SearchConfig } from 'tio-data/data/resources/reports.type';
import type { Store } from '@warp-drive/core';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type IntlService from 'ember-intl/services/intl';
import type { ComponentLike } from '@glint/template';

import { getSearchFieldsRequest, getSearchMappingRequest } from 'tio-data/requests/search';
import { query } from '@warp-drive/utilities/json-api';
import type {
  KeyLabelMapsResponse,
  OpenSearchMapping,
  SearchFieldPayload,
  SearchFieldsResponse,
} from 'tio-data/data/resources/search.ext';

interface ScopedFieldMetadataResponse {
  parsedFields: FieldOption[];
  rawFields: SearchFieldPayload[];
  sourceFieldByFieldKey: Record<string, string>;
  apiLabelByFieldKey: Record<string, string>;
  keyLabelMaps: KeyLabelMapsResponse;
}

interface ParsedFieldsResult {
  fields: FieldOption[];
  sourceFieldByFieldKey: Record<string, string>;
  apiLabelByFieldKey: Record<string, string>;
}

/**
 * Controller interface - apps provide their own controller that matches this signature
 */
export interface TasProgramsControllerInterface {
  // Pagination
  page: number;
  limit: number;

  // Dataset selection
  dataset?: string;

  // Filter properties
  programTemplateId?: string;
  programCreatedAtStart?: string;
  programCreatedAtEnd?: string;
  excludeNonVoucherPrograms?: boolean;
  courseCashProgramsOnly?: boolean;
  showIndividualApplications?: boolean;
  applicationStates?: string;
  applicationCreatedAtStart?: string;
  applicationCreatedAtEnd?: string;
  applicationDateRangePreset?: string;
  includePayments?: boolean;
  paymentStates?: string;
  paymentYear?: string;
  includeCourses?: boolean;
  coursesBeginDate?: string;
  coursesEndDate?: string;
  employeeId?: string;
  terminatedEmployees?: boolean;
  excludeTermVerified?: boolean;
  approverId?: string;
  includeInvoiceDate?: boolean;
  selectedColumns?: string;
  sortField?: string;

  // Method to update properties
  setProperties(updates: Record<string, unknown>): void;
}

export interface ProgramsPageSignature {
  Args: {
    company?: CompanyModel; // Optional for global analytics context
    controller: TasProgramsControllerInterface;

    // Route configuration
    baseRoute: string; // e.g., "authenticated.admin.tuition-assistance" or "authenticated.companies.show.tuition-assistance"
    applicationDetailRoute: string;
    programDetailRoute: string;
    programTemplateDetailRoute?: string;
    peopleDetailRoute?: string;
    companyDetailRoute?: string;
    financialInstitutionDetailRoute?: string;
    planDetailRoute?: string;
    documentDetailRoute?: string;
    assetDetailRoute?: string;

    // Optional customizations
    showViewToggle?: boolean; // Whether to show table/chart toggle
    showSaveReports?: boolean; // Whether to show save/load reports functionality
    showColumnSelector?: boolean; // Whether to show the Columns tab in the drawer (default: true)
    dataProviderComponent?: ComponentLike;
    filterColumnDependencies?: Record<string, string[]>; // Custom filter-to-column mappings
    isAdminContext?: boolean; // Whether this is being used in an admin context (shows admin-only fields)
    isGlobalContext?: boolean; // Whether this is global analytics (no company filter)
    initialReportId?: string | number; // Optional report id to auto-load on first render
    onCompanyChange?: (company?: CompanyModel) => void; // Optional callback when company selection changes in drawer
  };
  Blocks: {
    breadcrumbs?: [];
    headerActions?: [
      {
        downloadCsv: () => Promise<unknown>;
        currentView: 'table' | 'chart';
        onViewChange: (view: 'table' | 'chart') => void;
        selectedDataset: string;
        onDatasetChange: (dataset: string) => void;
      },
    ];
  };
}

export default class ProgramsPageComponent extends Component<ProgramsPageSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked currentView: 'table' | 'chart' = 'table';
  @tracked currentReport?: ReportModel;
  @tracked columnOrder: string[] = [];
  @tracked isSavingReport = false;
  @tracked private _savedConfigSnapshot = '';
  @tracked showPublishDialog = false;

  /** Chart configuration — extracted to a standalone tracked class. */
  chart = new ChartState();

  /**
   * Memoisation cache for selectedColumnsArray.
   * `trackedFunction` (and Glimmer autotracking in general) triggers downstream
   * re-runs whenever it sees a new object reference, even if the contents are
   * identical.  Because the getter re-derives a new `string[]` on every access
   * (array literals always create fresh references), the data provider perceives
   * a "change" and re-fires the OpenSearch query.
   *
   * By caching the previous result and returning the same reference when the
   * column list hasn't materially changed, we cut one full redundant search
   * round-trip on initial page load.
   */
  private _prevSelectedColumns: string[] = [];
  private _prevNormalizedSelectedColumnsKey = '';
  private _prevNormalizedSelectedColumns = new Set<string>();

  // Sidebar state
  @tracked isExpandedSidebar = true;

  // Resolved source from the latest search — used to hide fields that
  // would render empty from the column picker and table.
  @tracked _latestResolvedSource?: string[];

  // Drawer state
  @tracked selectedDrawerType: string | null = null;
  @tracked isDrawerOpen = false;
  @tracked activeFilterTab: 'filters' | 'columns' = 'filters';

  // Column removal warning modal
  @tracked showColumnWarningModal = false;
  @tracked columnToRemove: string | null = null;
  @tracked affectedFilters: string[] = [];

  private hasInitializedDynamicScope = false;
  private previousDynamicScopeCompanyId?: string;
  private previousDynamicScopeProgramTemplateId?: string;
  private scopedFieldMetadataCache = new Map<string, Promise<ScopedFieldMetadataResponse>>();
  private mappingErrorToastCache = new Set<string>();
  private pendingQueryUpdates: Record<string, unknown> = {};
  private pendingQueryUpdateTimer?: Timer;
  private static readonly QUERY_UPDATE_DEBOUNCE_MS = 180;

  // Responsive media query using tracked property
  @tracked private _isLargeScreen = false;

  // Generic dataset filters (for non-TAS datasets)
  @tracked genericFilters: Record<string, unknown> = {};

  get isTasDataset(): boolean {
    return this.chart.selectedDataset === 'tas-program-instances';
  }

  get effectiveShowIndividualApplications(): boolean {
    return this.isTasDataset ? Boolean(this.args.controller.showIndividualApplications) : true;
  }

  private resolveSearchModelType(dataset: string): string {
    if (dataset === 'tas-program-instances' && this.effectiveShowIndividualApplications) {
      return 'TasApplication';
    }

    return getModelTypeFromDataset(dataset);
  }

  constructor(owner: unknown, args: ProgramsPageSignature['Args']) {
    // @ts-expect-error - Owner type mismatch is ok here
    super(owner, args);
    this.setupMediaQuery();

    // Initialize chart dataset from controller query param (supports URL-based dataset selection)
    const controllerDataset = args.controller.dataset;
    if (controllerDataset) {
      this.chart.selectedDataset = controllerDataset;
    }
  }

  /***************************/
  /* Media Query Setup      */
  /***************************/

  private setupMediaQuery() {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    this._isLargeScreen = mediaQuery.matches;

    const handleMediaChange = (e: MediaQueryListEvent) => {
      this._isLargeScreen = e.matches;
    };

    mediaQuery.addEventListener('change', handleMediaChange);

    registerDestructor(this, () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
      if (this.pendingQueryUpdateTimer !== undefined) {
        // eslint-disable-next-line ember/no-runloop -- runloop debounce timer, deterministically torn down here via @ember/destroyable's registerDestructor
        cancel(this.pendingQueryUpdateTimer);
      }
    });
  }

  get isLargeScreen() {
    return this._isLargeScreen;
  }

  /***************************/
  /* Program Templates      */
  /***************************/

  programTemplatesData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    const companyId = this.args.company?.id;
    if (!companyId) {
      return [];
    }
    const { content } = (await this.store.request(
      query<TasProgramTemplateModel>('tas-program-template', {
        'filter[company]': companyId,
      })
    )) as { content: { data: TasProgramTemplateModel[] } };
    return content.data;
  });

  get selectedProgramTemplate(): TasProgramTemplateModel | undefined {
    if (!this.args.controller.programTemplateId || !this.programTemplatesData.isFinished) {
      return undefined;
    }
    return this.programTemplatesData.value?.find(
      (template: TasProgramTemplateModel) => template.id === this.args.controller.programTemplateId
    );
  }

  get companyId(): string | undefined {
    return this.args.company?.id ?? undefined;
  }

  /***************************/
  /* Partner Selection       */
  /***************************/

  get canFilterByPartner(): boolean {
    return Boolean(this.args.onCompanyChange) && Boolean(this.args.isAdminContext);
  }

  get isCompanyLocked(): boolean {
    return Boolean(this.currentReport?.isPublished);
  }

  get hasUnsavedChanges(): boolean {
    if (!this.currentReport) return false;
    if (!this._savedConfigSnapshot) return false;
    return JSON.stringify(this.reportSearchConfig) !== this._savedConfigSnapshot;
  }

  private markClean() {
    this._savedConfigSnapshot = JSON.stringify(this.reportSearchConfig);
  }

  get shouldShowNonTasPartnerFilter(): boolean {
    return !this.isTasDataset && this.canFilterByPartner;
  }

  partnerCompaniesData = trackedFunction(this, async (): Promise<CompanyModel[]> => {
    if (!this.canFilterByPartner) {
      return [];
    }

    const { content } = (await this.store.request(
      query<CompanyModel>('company', {
        'filter[is-active]': 1,
        'page[limit]': 400,
      })
    )) as { content: { data: CompanyModel[] } };
    return content.data;
  });

  get partnerCompanyItems(): CompanyModel[] {
    return this.partnerCompaniesData.value || [];
  }

  partnerCompanyDidChange = (key: string | null) => {
    if (!this.args.onCompanyChange) {
      return;
    }

    this.clearCompanyScopedCustomColumns();
    const company = key ? this.partnerCompanyItems.find((item) => item.id === key) : undefined;
    this.args.onCompanyChange(company);
  };

  private clearCompanyScopedCustomColumns(): void {
    const selectedColumns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];
    if (selectedColumns.length === 0) {
      return;
    }

    const byPath = this.availableFieldByPath;
    const remainingColumns = selectedColumns.filter((col) => {
      const field = byPath.get(col);
      return !(field?.isCustomField && !field.province);
    });

    if (remainingColumns.length !== selectedColumns.length) {
      this.setSelectedColumns(remainingColumns);
    }
  }

  private clearTemplateScopedCustomColumns(): void {
    const selectedColumns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];
    if (selectedColumns.length === 0) {
      return;
    }

    const byPath = this.availableFieldByPath;
    const remainingColumns = selectedColumns.filter((col) => {
      const field = byPath.get(col);
      return !(field?.isCustomField && !!field.province);
    });

    if (remainingColumns.length !== selectedColumns.length) {
      this.setSelectedColumns(remainingColumns);
    }
  }

  private syncDynamicCustomColumnScope(companyId?: string, programTemplateId?: string): void {
    if (!this.hasInitializedDynamicScope) {
      this.hasInitializedDynamicScope = true;
      this.previousDynamicScopeCompanyId = companyId;
      this.previousDynamicScopeProgramTemplateId = programTemplateId;
      return;
    }

    const companyChanged = this.previousDynamicScopeCompanyId !== companyId;
    const programTemplateChanged = this.previousDynamicScopeProgramTemplateId !== programTemplateId;

    if (companyChanged) {
      // Company changes invalidate both employee custom and template custom keys,
      // since templates belong to a company.
      this.clearCompanyScopedCustomColumns();
      this.clearTemplateScopedCustomColumns();
    } else if (programTemplateChanged) {
      // Template changes should only invalidate template-scoped custom keys.
      this.clearTemplateScopedCustomColumns();
    }

    this.previousDynamicScopeCompanyId = companyId;
    this.previousDynamicScopeProgramTemplateId = programTemplateId;
  }

  /***************************/
  /* Sidebar Management     */
  /***************************/

  toggleSidebar = () => {
    this.isExpandedSidebar = !this.isExpandedSidebar;
  };

  /***************************/
  /* Drawer Tab Management  */
  /***************************/

  selectFiltersTab = () => {
    this.activeFilterTab = 'filters';
  };

  selectColumnsTab = () => {
    this.activeFilterTab = 'columns';
  };

  /***************************/
  /* Drawer Management      */
  /***************************/

  openDrawer = (type: string) => {
    this.selectedDrawerType = type;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
    this.selectedDrawerType = null;
  };

  /***************************/
  /* Responsive Drawer Props */
  /***************************/

  get drawerBackdrop() {
    return this.isLargeScreen ? 'none' : 'faded';
  }

  get drawerCloseOnOutsideClick() {
    // Reimagined Drawer always uses a full-screen overlay, so
    // click-outside-to-close should always be enabled.
    return true;
  }

  get mainContentClasses() {
    // The reimagined Drawer portals to <body>, so we no longer push the main
    // content aside with margin-right.  The old 'drawer-open' class added
    // margin-right: 448px which compressed the underlying table/chart.
    return 'main-content-area';
  }

  /***************************/
  /* Selected Filters       */
  /***************************/

  get selectedFilters() {
    return {
      'Program Name': this.selectedProgramTemplate?.programName || 'All',
      'Program Creation Data': this.args.controller.programCreatedAtStart
        ? `From ${this.args.controller.programCreatedAtStart || 'Any'} to ${
            this.args.controller.programCreatedAtEnd || 'Any'
          }`
        : null,
      'Application State': this.args.controller.applicationStates || 'All',
      'Payment State': this.args.controller.paymentStates || 'All',
      'Application Creation Date': this.args.controller.applicationCreatedAtStart
        ? `From ${this.args.controller.applicationCreatedAtStart} to ${
            this.args.controller.applicationCreatedAtEnd || ''
          }`
        : null,
      'Include Courses in the Report': this.args.controller.includeCourses ? 'Yes' : 'No',
      'Include Payments Year': this.args.controller.paymentYear || 'All',
      'Include Courses': this.args.controller.includeCourses ? 'Yes' : 'No',
      'Employee ID Filter': this.args.controller.employeeId || '',
      'Terminated Employees': this.args.controller.terminatedEmployees ? 'Yes' : 'No',
      'Exclude Term Verified': this.args.controller.excludeTermVerified ? 'Yes' : 'No',
      'Approver ID Filter': this.args.controller.approverId || null,
      'Program Template Filter': this.selectedProgramTemplate?.id || null,
      'Include Terminated Employees': this.args.controller.terminatedEmployees ? 'Yes' : null,
      'Include Invoice Dates': this.args.controller.includeInvoiceDate ? 'Yes' : null,
      'Course Cash Programs Only': this.args.controller.courseCashProgramsOnly ? 'Yes' : null,
    };
  }

  get hasActiveAdvancedFilters() {
    const controller = this.args.controller;
    return !!(
      controller.showIndividualApplications ||
      controller.applicationStates ||
      controller.applicationCreatedAtStart ||
      controller.applicationCreatedAtEnd ||
      controller.includePayments ||
      controller.paymentStates ||
      controller.paymentYear ||
      controller.includeCourses ||
      controller.coursesBeginDate ||
      controller.coursesEndDate ||
      controller.employeeId ||
      controller.terminatedEmployees ||
      controller.excludeTermVerified ||
      controller.approverId ||
      controller.includeInvoiceDate ||
      controller.excludeNonVoucherPrograms ||
      controller.courseCashProgramsOnly
    );
  }

  /***************************/
  /* Column Selection       */
  /***************************/

  private isStructuredCollectionField(_fieldKey: string, type?: string): boolean {
    const normalizedType = (type || '').toLowerCase();
    return (
      normalizedType === 'object' ||
      normalizedType === 'flat_object' ||
      normalizedType === 'nested' ||
      normalizedType === 'array' ||
      normalizedType === 'list' ||
      normalizedType === 'json' ||
      normalizedType === 'map' ||
      normalizedType.startsWith('array') ||
      normalizedType.includes('nested')
    );
  }

  private isExcludedFieldKey(fieldKey: string): boolean {
    return (
      fieldKey === 'all_tas_application_ids_keyword' ||
      fieldKey === 'allTasApplicationIdsKeyword' ||
      fieldKey === 'all_tas_application_ids'
    );
  }

  private isObjectLikeFieldType(type?: string): boolean {
    return this.isStructuredCollectionField('', type);
  }

  private scopedFieldMetadataCacheKey(
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ): string {
    return [modelType, companyId || '__none__', programTemplateId || '__none__'].join('::');
  }

  private async fetchScopedFieldMetadata(
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ): Promise<ScopedFieldMetadataResponse> {
    const cacheKey = this.scopedFieldMetadataCacheKey(modelType, companyId, programTemplateId);
    const cached = this.scopedFieldMetadataCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request = (async () => {
      try {
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const startTime = now;

        // Single /fields call — embedded maps eliminate 2 extra HTTP requests.
        // WarpDrive wave 1.5: fetch via the shared builder (replaces
        // `store.adapterFor('search').fields`); unwrap `.content`.
        const { content: fieldsResponse } = await this.store.request<SearchFieldsResponse>(
          getSearchFieldsRequest(modelType, companyId, programTemplateId)
        );

        const elapsedMs =
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
        if (elapsedMs > 1500) {
          console.warn('[TAS Report] Field metadata load slow', {
            elapsedMs: Math.round(elapsedMs),
            modelType,
            companyId,
            programTemplateId,
          });
        }

        const {
          fields: parsedFields,
          sourceFieldByFieldKey,
          apiLabelByFieldKey,
        } = this.buildFieldOptionsFromFieldsResponse(fieldsResponse || { fields: [] });

        return {
          parsedFields,
          rawFields: fieldsResponse?.fields || [],
          sourceFieldByFieldKey,
          apiLabelByFieldKey,
          keyLabelMaps: fieldsResponse?.keyLabelMaps || {},
        };
      } catch (error) {
        this.scopedFieldMetadataCache.delete(cacheKey);
        throw error;
      }
    })();

    this.scopedFieldMetadataCache.set(cacheKey, request);
    return request;
  }

  // Fetch OpenSearch mapping dynamically to discover available fields
  mappingData = trackedFunction(this, async (): Promise<FieldOption[]> => {
    const companyId = this.args.company?.id || undefined;
    const programTemplateId = this.args.controller.programTemplateId || undefined;

    // Capture template data synchronously (before any await) so that
    // programTemplatesData is tracked as a reactive dependency of mappingData.
    // This ensures mappingData re-runs when template data finishes loading,
    // allowing template custom fields to appear in the column selector.
    void this.selectedProgramTemplate;
    void (this.programTemplatesData.value || []);

    this.syncDynamicCustomColumnScope(companyId, programTemplateId);

    // WarpDrive wave 1.5: preserves the previous `if (!searchAdapter)` fast
    // path — when no store is wired (e.g. some isolated component tests) fall
    // straight to the static catalog rather than throwing on `store.request`.
    if (!this.store) {
      return OpenSearchFieldMapper.getFallbackFields(this.chart.selectedDataset).map((field) => ({
        ...field,
        group: field.group || this.inferFieldGroup(field.key),
      }));
    }

    try {
      const modelType = this.resolveSearchModelType(this.chart.selectedDataset);

      // ── Company-scope optimisation ──────────────────────────────────
      // When the backend returns per-field templateLabels in a company-scope
      // /fields response we already have every field and every template's
      // labels.  On a subsequent template selection we can skip a second
      // HTTP call and just swap labels client-side via templateLabels.
      const companyCacheKey = this.scopedFieldMetadataCacheKey(modelType, companyId, undefined);
      const companyCached = this.scopedFieldMetadataCache.get(companyCacheKey);
      let metadata: Awaited<ReturnType<typeof this.fetchScopedFieldMetadata>>;
      let usePerFieldTemplateLabels = false;

      if (programTemplateId && companyCached) {
        const companyMeta = await companyCached;
        // Check if any raw field has templateLabels for the selected template
        const hasPerFieldLabels = companyMeta.rawFields.some(
          (f) => f.templateLabels?.[programTemplateId]
        );
        if (hasPerFieldLabels) {
          metadata = companyMeta;
          usePerFieldTemplateLabels = true;
        } else {
          metadata = await this.fetchScopedFieldMetadata(modelType, companyId, programTemplateId);
        }
      } else {
        metadata = await this.fetchScopedFieldMetadata(modelType, companyId, programTemplateId);
      }

      const { parsedFields, rawFields, sourceFieldByFieldKey, apiLabelByFieldKey, keyLabelMaps } =
        metadata;

      let fields: FieldOption[];

      if (usePerFieldTemplateLabels) {
        // Per-field templateLabels path: backend already resolved labels,
        // just overlay the selected template's specific labels.
        fields = applyPerFieldTemplateLabels(parsedFields, rawFields, programTemplateId);
      } else {
        // Legacy path: use sidecar maps for label resolution
        const companyEfKeyLabelMap = keyLabelMaps.companyEfKeyLabelMap || {};
        const isTasLikeDataset =
          this.chart.selectedDataset === 'tas-program-instances' ||
          this.chart.selectedDataset === 'tas-program-templates';

        const templateKeyLabelMap = isTasLikeDataset
          ? keyLabelMaps.tasTemplateKeyLabelMap || {}
          : {};
        const templateKeyProvinceMap = isTasLikeDataset
          ? keyLabelMaps.tasTemplateKeyProvinceMap || {}
          : {};

        fields = applyTemplateAndCustomFieldLabels(
          parsedFields,
          sourceFieldByFieldKey,
          apiLabelByFieldKey,
          companyEfKeyLabelMap,
          templateKeyLabelMap,
          templateKeyProvinceMap
        );
      }

      // Normalize post-resolved labels (including API/label-map overrides)
      // so pluralized variants dedupe consistently in the selector.
      const normalizedLabelFields = fields.map((field) => ({
        ...field,
        label: this.normalizeDisplayLabel(field.key, field.label, field.group) || field.label,
      }));

      const fieldsWithoutObjectRoots = normalizedLabelFields.filter(
        (field) =>
          !this.isEmployeeCustomRootFieldKey(field.key) && !this.isObjectLikeFieldType(field.type)
      );

      if (fieldsWithoutObjectRoots.length === 0) {
        throw new Error(`No fields found for ${modelType}`);
      }

      return fieldsWithoutObjectRoots;
    } catch (error) {
      console.error('Error fetching field data:', error);

      const modelType = this.resolveSearchModelType(this.chart.selectedDataset);
      const mappingErrorKey = this.scopedFieldMetadataCacheKey(
        modelType,
        companyId,
        programTemplateId
      );
      if (!this.mappingErrorToastCache.has(mappingErrorKey)) {
        this.mappingErrorToastCache.add(mappingErrorKey);
        this.notifications.add('Failed to load column mappings from server', {
          appearance: 'error',
        });
      }

      try {
        // WarpDrive wave 1.5: fetch the mapping via the shared builder
        // (replaces `store.adapterFor('search').mapping`); unwrap `.content`.
        const { content: mapping } = await this.store.request<OpenSearchMapping>(
          getSearchMappingRequest(modelType)
        );
        if (mapping) {
          return OpenSearchFieldMapper.parseMapping(mapping);
        }
      } catch (mappingError) {
        console.error('Error fetching mapping fallback:', mappingError);
      }

      return OpenSearchFieldMapper.getFallbackFields(this.chart.selectedDataset).map((field) => ({
        ...field,
        group: field.group || this.inferFieldGroup(field.key),
      }));
    }
  });

  private buildFieldOptionsFromFieldsResponse(response: SearchFieldsResponse): ParsedFieldsResult {
    const fields = response.fields || [];
    const sourceFieldByFieldKey: Record<string, string> = {};
    const apiLabelByFieldKey: Record<string, string> = {};

    const parsedFields = fields
      .filter((field) => Boolean(field.name))
      .filter((field) => field.isDisplayable !== false)
      .filter((field) => !this.isExcludedFieldKey(field.name))
      .filter(
        (field) =>
          !this.isObjectLikeFieldType(field.type) || this.isAllowedObjectLikeCustomField(field.name)
      )
      .filter((field) => !this.isEmployeeCustomRootFieldKey(field.name))
      // Filter out nested/object-style dot paths. Keep only dot paths that
      // represent flattened scalar custom fields.
      .filter((field) => this.isAllowedDotPathField(field.name))
      .map((field) => {
        const name = field.name;
        const type = field.type || 'keyword';
        const explicitComponentHint =
          (field as SearchFieldPayload & { uiHint?: { componentType?: string } }).uiHint
            ?.componentType ||
          (field as SearchFieldPayload & { uiHints?: { componentType?: string } }).uiHints
            ?.componentType;
        const componentTypeHint = OpenSearchFieldMapper.resolveComponentTypeHint(
          name,
          explicitComponentHint
        );
        const sourceField =
          field.sourceField || (field as { source_field?: string | undefined }).source_field;
        const sanitizedApiLabel = this.sanitizeApiLabel(name, field.displayLabel || field.label);
        if (sourceField) {
          sourceFieldByFieldKey[name] = sourceField;
        }
        if (sanitizedApiLabel) {
          apiLabelByFieldKey[name] = sanitizedApiLabel;
        }

        const capabilities = field.capabilities;
        const uiHints = field.uiHints;
        const group = this.inferDisplayGroup(name, field.isCustom);

        const isObjectLike = this.isObjectLikeFieldType(type);

        return {
          key: name,
          // Preserve the canonical (often snake_case) path alongside the
          // camelCase `name`. Per-field templateLabels and some default-column
          // lookups are keyed by path; without this, overlays silently miss.
          path: field.path,
          label: this.resolveColumnLabel(name, sanitizedApiLabel, group),
          type,
          group,
          isDate: type === 'date',
          isTimestamp: type !== 'date' && OpenSearchFieldMapper.isTimestampField(name),
          adminOnly: OpenSearchFieldMapper.isAdminOnlyField(name),
          // Map backend capabilities to FieldOption flags
          isFilterable: capabilities?.filter ?? !isObjectLike,
          isSortable: capabilities?.sort ?? false,
          isGroupable: capabilities?.aggregate ?? false,
          isRangeQueryable: capabilities?.range ?? false,
          isFullTextSearchable: capabilities?.fullText ?? type === 'text',
          isCustomField: field.isCustom,
          semanticRole: field.semanticRole,
          reportDatatype: field.reportDatatype ?? type,
          enumValues: field.enumValues,
          // UI rendering hints from backend
          uiHints: uiHints
            ? {
                filterWidget: uiHints.filterWidget,
                multiSelect: uiHints.multiSelect,
              }
            : undefined,
          ...(componentTypeHint ? { componentTypeHint } : {}),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // Apply FE-defined enum values for known fields that the backend
    // doesn't populate (e.g., PSLF form_status, SYF enrollment_state).
    // Backend-provided enumValues always take precedence.
    const enrichedFields = applyFallbackEnumValues(parsedFields, this.chart.selectedDataset);

    return {
      fields: enrichedFields,
      sourceFieldByFieldKey,
      apiLabelByFieldKey,
    };
  }

  private inferDisplayGroup(fieldName: string, isCustomField?: boolean): string {
    const modelType = this.resolveSearchModelType(this.chart.selectedDataset);
    const baseGroup = sharedGetDisplayGroup(fieldName, modelType, isCustomField);
    return this.remapTasGroup(fieldName, baseGroup);
  }

  /**
   * Delegates to the shared getFieldGroup utility in label-generation.ts,
   * passing the current dataset's model type for dataset-aware grouping.
   */
  private inferFieldGroup(fieldName: string): string {
    const modelType = this.resolveSearchModelType(this.chart.selectedDataset);
    const baseGroup = sharedGetFieldGroup(fieldName, modelType);
    return this.remapTasGroup(fieldName, baseGroup);
  }

  private remapTasGroup(fieldName: string, group: string): string {
    const reviewQueueRemapped = this.remapTasReviewQueueGroup(fieldName, group);
    return this.remapTasProgramGrouping(fieldName, reviewQueueRemapped);
  }

  private shouldGroupUnderReviewQueue(fieldName: string): boolean {
    const normalized = fieldName.toLowerCase();

    if (
      /withdraw|approv|review|queue|evidence|attachment|digital_asset|uploaded_file|file_name|file_url|fax|priority|reviewer|approver|(^|[_.])tas_asset_/.test(
        normalized
      )
    ) {
      return true;
    }

    // Keep submission status-like fields out of this remap unless they're
    // timestamp/date fields.
    return /submit|submission/.test(normalized) && /timestamp|date|time|_at\b/.test(normalized);
  }

  private remapTasReviewQueueGroup(fieldName: string, group: string): string {
    if (!this.isTasDataset || !this.shouldGroupUnderReviewQueue(fieldName)) {
      return group;
    }

    const isCustomGroup = /\/\s*custom$/i.test(group);
    return isCustomGroup ? 'Review & Queue / Custom' : 'Review & Queue';
  }

  private remapTasProgramGrouping(fieldName: string, group: string): string {
    if (!this.isTasDataset) {
      return group;
    }

    const isCustomGroup = /\/\s*custom$/i.test(group);
    const baseGroup = group.replace(/\/\s*custom$/i, '').trim();

    if (baseGroup === 'Program Builder Setup') {
      return isCustomGroup ? 'Program Template / Custom' : 'Program Template';
    }

    if (baseGroup === 'Program Details') {
      const targetGroup = this.resolveTasProgramDetailsTargetGroup(fieldName);
      return isCustomGroup ? `${targetGroup} / Custom` : targetGroup;
    }

    return group;
  }

  private resolveTasProgramDetailsTargetGroup(
    fieldName: string
  ): 'Program Template' | 'Program Instance' {
    const normalized = fieldName.toLowerCase();

    if (
      /program_instance|tas_program_instance|tasprograminstance|tas_pi_|\binstance\b/.test(
        normalized
      )
    ) {
      return 'Program Instance';
    }

    return 'Program Template';
  }

  private isEmployeeCustomRootFieldKey(fieldKey: string): boolean {
    const normalized = this.canonicalEmployeeContainerKey(fieldKey);
    if (
      normalized === 'employeeeffields' ||
      normalized === 'employeecustomfields' ||
      normalized === 'employeefields'
    ) {
      return true;
    }

    if (!fieldKey.includes('.')) {
      return false;
    }

    const segments = fieldKey
      .split('.')
      .filter(Boolean)
      .map((segment) => {
        const compact = segment.replace(/_/g, '').toLowerCase();
        return compact.startsWith('all') ? compact.slice(3) : compact;
      });

    const leaf = segments.at(-1);
    const parent = segments.at(-2);
    if (!leaf || !parent) {
      return false;
    }

    const isContainerLeaf = leaf === 'employeecustomfields' || leaf === 'employeefields';
    const isEmployeeContainerParent =
      parent === 'employeeeffields' ||
      parent === 'employeecustomfields' ||
      parent === 'employeefields';

    return isContainerLeaf && isEmployeeContainerParent;
  }

  private isEmployeeCustomFieldKey(fieldKey: string): boolean {
    const compact = fieldKey.replace(/_/g, '').toLowerCase();
    const [parent] = compact.split('.');
    if (!parent) {
      return false;
    }

    const canonicalParent = parent.startsWith('all') ? parent.slice(3) : parent;
    return (
      canonicalParent === 'employeeeffields' ||
      canonicalParent === 'employeecustomfields' ||
      canonicalParent === 'employeefields'
    );
  }

  private isAllowedDotPathField(fieldName: string): boolean {
    if (!fieldName.includes('.')) {
      return true;
    }

    const parent = fieldName.split('.')[0] || '';
    const normalizedParent = parent.replace(/_/g, '').toLowerCase();
    if (
      normalizedParent.includes('customfields') ||
      normalizedParent.includes('effields') ||
      normalizedParent.includes('employeefields')
    ) {
      return true;
    }

    return (
      // Custom fields with or without _flattened suffix: e.g.
      // tas_program_instance_custom_fields_flattened, tas_application_custom_fields
      /^(tas_)?(program_instance|program_template|application|applicant|course)_custom_fields(_flattened)?$/i.test(
        parent
      ) ||
      // Non-flattened standard fields (no top-level keyword alias conflict):
      // e.g. tas_program_instance_fields. The _flattened variant is excluded
      // because it mirrors top-level _keyword fields without being populated
      // in search results, which would produce blank columns.
      /^(tas_)?(program_instance|program_template|application|applicant|course)_fields$/i.test(
        parent
      ) ||
      /^(tas)?(ProgramInstance|ProgramTemplate|Application|Applicant|Course)CustomFields(Flattened)?$/.test(
        parent
      ) ||
      /^(tas)?(ProgramInstance|ProgramTemplate|Application|Applicant|Course)Fields$/.test(parent)
    );
  }

  private isAllowedObjectLikeCustomField(fieldName: string): boolean {
    if (!fieldName.includes('.')) {
      return false;
    }

    const parent = fieldName.split('.')[0] || '';
    const normalizedParent = parent.replace(/_/g, '').toLowerCase();
    return (
      normalizedParent.includes('customfields') ||
      normalizedParent.includes('effields') ||
      normalizedParent.includes('employeefields')
    );
  }

  private sanitizeApiLabel(fieldKey: string, apiLabel?: string): string | undefined {
    const trimmed = apiLabel?.trim();
    if (!trimmed) {
      return undefined;
    }

    // Employee custom labels that collapse to a single letter are typically
    // flat_object internals leaking through label maps (e.g. "S").
    if (
      trimmed.length === 1 &&
      (this.isEmployeeCustomFieldKey(fieldKey) || fieldKey.toLowerCase().includes('flattened'))
    ) {
      return undefined;
    }

    return trimmed.replace(/\s+/g, ' ');
  }

  private normalizeTechnicalLabelSuffixes(label: string): string {
    return label
      .replace(/(?:\s+(?:keyword|analyzable|text|nested|flattened|flat\s*object))+$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private applyWordCase(template: string, value: string): string {
    if (template === template.toUpperCase()) {
      return value.toUpperCase();
    }

    if (template[0] && template[0] === template[0].toUpperCase()) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }

  private singularizeTrailingWord(word: string): string {
    const lower = word.toLowerCase();

    // Keep common singular endings that happen to end in "s".
    if (lower.endsWith('ss') || lower.endsWith('us') || lower.endsWith('is')) {
      return word;
    }

    let singular = lower;
    if (lower.endsWith('ies') && lower.length > 3) {
      singular = `${lower.slice(0, -3)}y`;
    } else if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      singular = lower.slice(0, -2);
    } else if (lower.endsWith('s') && lower.length > 1) {
      singular = lower.slice(0, -1);
    }

    return this.applyWordCase(word, singular);
  }

  private normalizeNamePlural(label: string): string {
    return label.replace(/\b([A-Za-z]+)\b$/, (match, word: string) => {
      return this.singularizeTrailingWord(word || match);
    });
  }

  private shouldDropEmployeePrefix(fieldKey: string, group?: string): boolean {
    const normalizedGroup = (group || '').toLowerCase();
    if (normalizedGroup.startsWith('employee')) {
      return true;
    }

    return this.isEmployeeCustomFieldKey(fieldKey) || /(^|[._])employee/i.test(fieldKey);
  }

  private normalizeDisplayLabel(fieldKey: string, label: string, group?: string): string {
    let normalized = label.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return normalized;
    }

    normalized = this.normalizeTechnicalLabelSuffixes(normalized);

    if (this.shouldDropEmployeePrefix(fieldKey, group)) {
      normalized = normalized.replace(/^Employee\s+/i, '').trim();
    }

    // Collapse repeated leading word (e.g. "Employee Employee Status Type" → "Employee Status Type")
    normalized = normalized.replace(/^(\w+)\s+\1\b/i, '$1').trim();

    normalized = this.normalizeNamePlural(normalized);
    return normalized.replace(/\s+/g, ' ').trim();
  }

  private resolveColumnLabel(fieldKey: string, apiLabel?: string, group?: string): string {
    const cleanApiLabel = this.sanitizeApiLabel(fieldKey, apiLabel);
    if (cleanApiLabel) {
      const normalizedApiLabel = this.normalizeDisplayLabel(fieldKey, cleanApiLabel, group);
      if (normalizedApiLabel) {
        return normalizedApiLabel;
      }
    }

    const generated = OpenSearchFieldMapper.formatFieldLabel(fieldKey).trim();
    if (generated) {
      const normalizedGeneratedLabel = this.normalizeDisplayLabel(fieldKey, generated, group);
      if (normalizedGeneratedLabel) {
        return normalizedGeneratedLabel;
      }
    }

    const leaf = fieldKey.split('.').filter(Boolean).at(-1);
    const fallback = this.normalizeDisplayLabel(
      fieldKey,
      (leaf || fieldKey).replace(/[_-]+/g, ' ').trim(),
      group
    );
    return fallback || fieldKey;
  }

  private canonicalEmployeeContainerKey(fieldKey: string): string {
    const normalized = stripTypeSuffixes(fieldKey).replace(/[_.]/g, '').toLowerCase();
    return normalized.startsWith('all') ? normalized.slice(3) : normalized;
  }

  private isTasNonProgramInstanceScopedFieldKey(field: FieldOption): boolean {
    return !!field.province && field.province !== 'PROGRAM';
  }

  private get datasetScopedFields(): FieldOption[] {
    // Show error/loading state
    if (this.mappingData.error || this.mappingData.isLoading || !this.mappingData.value) {
      return [];
    }

    // Start from mapped fields and apply dataset-level visibility rules.
    // Keep structured/object fields here so they can remain available to filters.
    const baseFields = this.mappingData.value.filter(
      (field) => !this.isExcludedFieldKey(field.key)
    );
    const isTasDataset = this.isTasDataset;

    const deprecatedFields = new Set([
      'tasApplicationCoursesBeginDate',
      'tasApplicationCoursesEndDate',
      'tasProgramInstanceFieldLockBalanceMultiplierAtStartOfFiscalYearBoolean',
      'paymentFirstDate',
    ]);

    const aggregateOnlyFields = new Set(['total_applications', 'applications']);
    const isAdminContext = this.args.isAdminContext ?? false;

    if (!isTasDataset) {
      return baseFields
        .filter((field) => isAdminContext || !field.adminOnly)
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (this.effectiveShowIndividualApplications) {
      return baseFields
        .filter(
          (field) =>
            !aggregateOnlyFields.has(field.key) &&
            !deprecatedFields.has(field.key) &&
            (isAdminContext || !field.adminOnly)
        )
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    const filteredFields = baseFields
      .filter(
        (field) =>
          !deprecatedFields.has(field.key) &&
          !this.isTasNonProgramInstanceScopedFieldKey(field) &&
          (isAdminContext || !field.adminOnly)
      )
      .sort((a, b) => a.label.localeCompare(b.label));

    const hasAggregateFields = filteredFields.some((f) => aggregateOnlyFields.has(f.key));
    if (!hasAggregateFields) {
      return [
        ...filteredFields,
        { key: 'total_applications', label: 'Total Applications', type: 'text', group: 'Other' },
        { key: 'applications', label: 'Applications Count', type: 'text', group: 'Other' },
      ].sort((a, b) => a.label.localeCompare(b.label));
    }

    return filteredFields;
  }

  get availableFields(): FieldOption[] {
    // Column selector + table should only expose scalar fields.
    return this.datasetScopedFields.filter(
      (field) => !this.isStructuredCollectionField(field.key, field.type)
    );
  }

  /**
   * Available fields narrowed to those the backend actually resolved in the
   * latest search.  Used by the column picker and "select all" so users only
   * see fields that would produce non-empty values.
   *
   * Before the first search completes (resolvedSource is undefined) the full
   * `availableFields` list is returned so the picker isn't empty on load.
   */
  get columnsAvailableFields(): FieldOption[] {
    return filterFieldsByResolvedSource(this.availableFields, this._latestResolvedSource);
  }

  get availableFilterFields(): FieldOption[] {
    // Filters can legitimately rely on list/object-backed fields when the
    // backend marks them as filterable via metadata.
    return this.datasetScopedFields;
  }

  get selectedColumnsArray(): string[] {
    const explicitColumns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];

    // When no columns are explicitly selected, use per-dataset defaults.
    // This prevents the table from showing ALL columns (overwhelming) and
    // ensures each dataset starts with a sensible set of visible columns.
    let columns =
      explicitColumns.length > 0 ? explicitColumns : this.getDefaultColumnsForCurrentDataset();

    if (this.effectiveShowIndividualApplications && columns.length === 0) {
      // For non-TAS datasets with no explicit column selection, use curated defaults
      // so the table doesn't dump every field from the index.
      const dataset = this.chart.selectedDataset || 'tas-program-instances';
      const defaults = getDefaultColumns(dataset);
      if (defaults.length > 0) {
        columns = defaults;
      }
    }

    // Collapse semantic duplicates (e.g. plural/singular variants) to a single
    // canonical key so table/query rendering never shows all variants at once.
    columns = this.canonicalizeSelectedColumns(columns);
    columns = this.sanitizeSelectedColumns(columns);

    // Return the cached reference when the column list is materially unchanged.
    // This prevents the data provider's trackedFunction from seeing a new array
    // reference and re-firing the search query unnecessarily.
    const prev = this._prevSelectedColumns;
    if (columns.length === prev.length && columns.every((col, i) => col === prev[i])) {
      return prev;
    }

    // eslint-disable-next-line ember/no-side-effects -- deliberate identity-preserving memo cache on a non-tracked private field (returns a stable reference when contents are unchanged)
    this._prevSelectedColumns = columns;
    return columns;
  }

  /**
   * Get default columns for the current dataset, filtered to only include
   * columns that actually exist in the available fields.
   *
   * Matching is suffix-agnostic: type suffixes like `_keyword`, `_date`, etc.
   * are stripped from both the default keys and the available field keys before
   * comparison.  This ensures defaults work regardless of whether the available
   * fields come from the fields API (suffixed), the mapping endpoint, or the
   * static fallback (unsuffixed).
   *
   * If none of the hard-coded defaults match, the first N available fields are
   * returned so the table always starts with a manageable column set.
   */
  private getDefaultColumnsForCurrentDataset(): string[] {
    // Computed aggregation columns are added unconditionally by the aggregate
    // table, so they must not appear in the selectable defaults.
    const AGGREGATION_KEYS = Object.keys(AGGREGATION_COLUMNS);

    // For TAS aggregate mode, use aggregate-specific data columns so the
    // column selector reflects what the aggregate table actually displays.
    const defaults =
      this.isTasDataset && !this.effectiveShowIndividualApplications
        ? DEFAULT_COLUMNS.aggregate.filter((key) => !AGGREGATION_KEYS.includes(key))
        : getDefaultColumns(this.chart.selectedDataset);
    const available = this.availableFields;

    // If fields haven't loaded yet, return the raw defaults
    if (available.length === 0) {
      return defaults;
    }

    // Match defaults against available fields by path or key
    const byPath = this.availableFieldByPath;
    const matched = defaults.filter((key) => byPath.has(key));

    if (matched.length > 0) {
      return matched;
    }

    // No defaults matched — pick first N available fields as a reasonable starting set
    const MAX_DEFAULT_COLUMNS = 6;
    return available.slice(0, MAX_DEFAULT_COLUMNS).map((f) => f.path || f.key);
  }

  private get availableFieldByPath(): Map<string, FieldOption> {
    const byKey = new Map<string, FieldOption>();

    for (const field of this.availableFields) {
      // Index by canonical path first
      if (field.path && !byKey.has(field.path)) {
        byKey.set(field.path, field);
      }
      // Also index by key for backward compatibility with saved column selections
      if (!byKey.has(field.key)) {
        byKey.set(field.key, field);
      }
    }

    return byKey;
  }

  private canonicalizeSelectedColumns(columns: string[]): string[] {
    if (columns.length <= 1) {
      return columns;
    }

    // Deduplicate by canonical path: two columns that resolve to the same
    // field (e.g. camelCase key and snake_case path) collapse to one entry.
    const byPath = this.availableFieldByPath;
    const seenPaths = new Set<string>();
    const canonical: string[] = [];

    for (const col of columns) {
      const field = byPath.get(col);
      const canonicalKey = field?.path || col;
      if (!seenPaths.has(canonicalKey)) {
        seenPaths.add(canonicalKey);
        canonical.push(col);
      }
    }

    return canonical;
  }

  private sanitizeSelectedColumns(columns: string[]): string[] {
    if (columns.length === 0) {
      return columns;
    }

    const filtered = columns.filter((column) => !this.isExcludedFieldKey(column));

    const byPath = this.availableFieldByPath;
    if (byPath.size === 0) {
      // Fields not loaded yet — pass through but drop structured collections
      return filtered.filter((column) => !this.isStructuredCollectionField(column));
    }

    return filtered.filter((column) => {
      const field = byPath.get(column);
      if (!field) {
        return false;
      }
      if (this.isStructuredCollectionField(field.key, field.type)) {
        return false;
      }
      // In TAS aggregate mode, drop non-program-instance columns
      if (this.isTasDataset && !this.effectiveShowIndividualApplications) {
        if (this.isTasNonProgramInstanceScopedFieldKey(field)) {
          return false;
        }
      }
      return true;
    });
  }

  setSelectedColumns = (columns: string[]) => {
    const nextSelectedColumns = this.sanitizeSelectedColumns(
      this.canonicalizeSelectedColumns(columns)
    ).join(',');

    this.updateQueryParameters({ selectedColumns: nextSelectedColumns });
  };

  /**
   * Last resolvedSource we acted on, keyed by joined contents. Prevents
   * re-running pruning when the data provider re-yields the same value.
   */
  private _lastReconciledResolvedSourceKey?: string;

  /**
   * Drop selected columns whose canonical path the backend did not
   * recognize for the most recent search. The backend already expands
   * wildcards and coerces `_keyword`/dotted-container aliases when it
   * builds `field_meta.resolvedSource`, so any selectedColumn whose
   * normalized key is absent from that set would render blank.
   *
   * Matching uses the same suffix-stripped + normalized form used
   * elsewhere in this component, plus a dotted-prefix check so a
   * container-level selection like `employee_ef_fields.employee_custom_fields`
   * still matches when the backend resolved leaf paths beneath it.
   */
  reconcileSelectedColumnsFromResolvedSource = (resolvedSource?: string[]) => {
    if (!Array.isArray(resolvedSource) || resolvedSource.length === 0) {
      return;
    }

    // Persist for column-picker filtering (hides empty-value fields).
    this._latestResolvedSource = resolvedSource;

    const key = resolvedSource.join('\u0000');
    if (key === this._lastReconciledResolvedSourceKey) {
      return;
    }
    this._lastReconciledResolvedSourceKey = key;

    const current = this.selectedColumnsArray;
    if (current.length === 0) {
      return;
    }

    const kept = pruneSelectedColumnsByResolvedSource(current, resolvedSource);
    if (kept.length === current.length) {
      return;
    }

    // Only prune — never add back. setSelectedColumns runs through
    // sanitizeSelectedColumns which itself drops anything not in
    // availableFields, so this stays defensive.
    this.setSelectedColumns(kept);
  };

  private get selectedColumnsSet(): Set<string> {
    const key = this.selectedColumnsArray.join('\u0000');

    if (key === this._prevNormalizedSelectedColumnsKey) {
      return this._prevNormalizedSelectedColumns;
    }

    /* eslint-disable ember/no-side-effects -- deliberate key-based memo cache on non-tracked private fields */
    this._prevNormalizedSelectedColumnsKey = key;
    this._prevNormalizedSelectedColumns = new Set(this.selectedColumnsArray);
    /* eslint-enable ember/no-side-effects */

    return this._prevNormalizedSelectedColumns;
  }

  /***************************/
  /* Filter Management      */
  /***************************/

  flushPendingQueryUpdates = () => {
    const batchedUpdates = this.pendingQueryUpdates;
    this.pendingQueryUpdates = {};
    this.pendingQueryUpdateTimer = undefined;

    if (Object.keys(batchedUpdates).length === 0) {
      return;
    }

    this.args.controller.setProperties(batchedUpdates);
    this.autoEnableColumnsForFilters(batchedUpdates);
  };

  private valuesEqual(a: unknown, b: unknown): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }

      return a.every((value, index) => value === b[index]);
    }

    return a === b;
  }

  private getCurrentControllerValue(key: string): unknown {
    return (this.args.controller as unknown as Record<string, unknown>)[key];
  }

  private pruneNoOpUpdates(updates: Record<string, unknown>): Record<string, unknown> {
    const next: Record<string, unknown> = {};

    Object.entries(updates).forEach(([key, value]) => {
      const pendingValue = this.pendingQueryUpdates[key];
      const baselineValue =
        pendingValue !== undefined ? pendingValue : this.getCurrentControllerValue(key);

      if (!this.valuesEqual(baselineValue, value)) {
        next[key] = value;
      }
    });

    return next;
  }

  updateQueryParameters = (updates: Record<string, unknown>) => {
    const effectiveUpdates = this.pruneNoOpUpdates(updates);
    if (Object.keys(effectiveUpdates).length === 0) {
      return;
    }

    this.pendingQueryUpdates = {
      ...this.pendingQueryUpdates,
      ...effectiveUpdates,
    };

    if (this.pendingQueryUpdateTimer !== undefined) {
      // eslint-disable-next-line ember/no-runloop -- pairs with the debounce below; timer is tracked + torn down via registerDestructor
      cancel(this.pendingQueryUpdateTimer);
    }

    // Coalesce rapid filter updates (especially during initial hydration)
    // to avoid firing multiple equivalent search requests in succession.
    // eslint-disable-next-line ember/no-runloop -- intentional runloop debounce (test-aware via settled()); cleanup handled in registerDestructor
    this.pendingQueryUpdateTimer = debounce(
      this,
      this.flushPendingQueryUpdates,
      ProgramsPageComponent.QUERY_UPDATE_DEBOUNCE_MS
    );
  };

  /**
   * Update generic filters for non-TAS datasets.
   * Merges partial updates into the tracked filters object and resets to page 1.
   */
  updateGenericFilters = (updates: Record<string, unknown>) => {
    this.genericFilters = { ...this.genericFilters, ...updates };
    this.args.controller.setProperties({ page: 1 });
  };

  /**
   * Clear all generic filters.
   */
  clearGenericFilters = () => {
    this.genericFilters = {};
    this.args.controller.setProperties({ page: 1 });
  };

  /**
   * Get the list of filterable fields from the current dataset's available fields.
   */
  get filterableFields(): FieldOption[] {
    return this.availableFilterFields.filter((f) => f.isFilterable);
  }

  /**
   * Default filter-to-column dependencies — auto-derived from the filter registry.
   * Can be overridden by passing @filterColumnDependencies.
   */
  private get filterColumnDependencies(): Record<string, string[]> {
    return this.args.filterColumnDependencies ?? deriveFilterColumnDependencies();
  }

  /**
   * Flat list of field keys available in the current dataset.
   *
   * Used to determine which filter sections are relevant — sections whose
   * field patterns don't match any dataset field are hidden entirely.
   * This is intentionally the *dataset* fields (what the backend provides),
   * not the user's *selected* columns. Column-level protection is handled
   * separately by the filter-column dependency modals.
   */
  get availableFieldKeys(): string[] {
    return this.availableFilterFields.map((f) => f.key);
  }

  private getControllerValue = <K extends keyof TasProgramsControllerInterface>(
    key: K
  ): TasProgramsControllerInterface[K] => this.args.controller[key];

  private autoEnableColumnsForFilters(filterUpdates: Record<string, unknown>) {
    const currentColumns = new Set(this.selectedColumnsArray);
    let columnsAdded = false;

    Object.entries(filterUpdates).forEach(([filterKey, filterValue]) => {
      const isFilterActive = this.hasActiveFilterValue(filterValue);

      // Special handling for paymentStates
      if (filterKey === 'paymentStates' && isFilterActive && typeof filterValue === 'string') {
        const selectedStates = filterValue.split(',').filter(Boolean);
        selectedStates.forEach((state) => {
          if (!currentColumns.has(state)) {
            currentColumns.add(state);
            columnsAdded = true;
          }
        });
        return;
      }

      // Standard handling
      const requiredColumns = this.filterColumnDependencies[filterKey];
      if (requiredColumns && isFilterActive) {
        requiredColumns.forEach((columnKey: string) => {
          if (!currentColumns.has(columnKey)) {
            currentColumns.add(columnKey);
            columnsAdded = true;
          }
        });
      }
    });

    if (columnsAdded) {
      this.setSelectedColumns(Array.from(currentColumns));
    }
  }

  private hasActiveFilterValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (typeof value === 'boolean') {
      return value === true;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }

  /***************************/
  /* Column Selection UI    */
  /***************************/

  /**
   * Check if a field is selected by exact key match
   */
  isColumnSelected = (fieldKey: string) => {
    return this.selectedColumnsSet.has(fieldKey);
  };

  /**
   * Get list of active filters that depend on a specific column
   */
  private getFiltersRequiringColumn = (columnKey: string): string[] => {
    const affectedFilters: string[] = [];

    Object.entries(this.filterColumnDependencies).forEach(([filterKey, requiredColumns]) => {
      const controllerKey = filterKey as keyof TasProgramsControllerInterface;
      const filterValue = this.getControllerValue(controllerKey);
      if (!this.hasActiveFilterValue(filterValue)) {
        return;
      }

      if (requiredColumns.includes(columnKey)) {
        affectedFilters.push(filterKey);
      }
    });

    // Special handling for paymentStates
    const paymentStates = this.args.controller.paymentStates;
    if (paymentStates && typeof paymentStates === 'string') {
      const selectedStates = paymentStates.split(',').filter(Boolean);
      if (selectedStates.includes(columnKey)) {
        affectedFilters.push('paymentStates');
      }
    }

    return affectedFilters;
  };

  /**
   * Handle column toggle click - checks if warning is needed before toggling
   * Shows modal if column is required by active filters
   */
  handleColumnToggle = (columnKey: string): void => {
    const currentColumns = new Set(this.selectedColumnsArray);

    // Check if we're trying to remove an existing column (it's currently selected)
    if (currentColumns.has(columnKey)) {
      // Check if this column is required by any active filters
      const requiredByFilters = this.getFiltersRequiringColumn(columnKey);

      if (requiredByFilters.length > 0) {
        // Show warning modal instead of toggling immediately
        this.columnToRemove = columnKey;
        this.affectedFilters = requiredByFilters;
        this.showColumnWarningModal = true;
        return; // Don't toggle - wait for user confirmation
      }
    }

    // Safe to toggle immediately
    this.toggleColumn(columnKey);
  };

  toggleColumn = (columnKey: string) => {
    const currentColumns = new Set(this.selectedColumnsArray);

    if (currentColumns.has(columnKey)) {
      currentColumns.delete(columnKey);
    } else {
      currentColumns.add(columnKey);
    }

    this.setSelectedColumns(Array.from(currentColumns));
  };

  /**
   * Confirm removal of column and clear affected filters
   */
  confirmColumnRemoval = () => {
    if (!this.columnToRemove) return;

    const currentColumns = new Set(this.selectedColumnsArray);
    currentColumns.delete(this.columnToRemove);
    this.setSelectedColumns(Array.from(currentColumns));

    // Clear affected filters
    const updates: Record<string, unknown> = {};
    this.affectedFilters.forEach((filterKey) => {
      if (filterKey === 'paymentStates') {
        const states = this.args.controller.paymentStates?.split(',').filter(Boolean) || [];
        const remainingStates = states.filter((state) => state !== this.columnToRemove);
        updates.paymentStates = remainingStates.join(',');
      } else {
        updates[filterKey] = null;
      }
    });

    if (Object.keys(updates).length > 0) {
      this.updateQueryParameters(updates);
    }

    this.cancelColumnRemoval();
  };

  /**
   * Cancel column removal
   */
  cancelColumnRemoval = () => {
    this.showColumnWarningModal = false;
    this.columnToRemove = null;
    this.affectedFilters = [];
  };

  selectAllColumns = () => {
    this.setSelectedColumns(this.columnsAvailableFields.map((f) => f.key));
  };

  deselectAllColumns = () => {
    this.setSelectedColumns([]);
  };

  get groupedFields(): Array<[string, FieldOption[]]> {
    return groupFieldOptions(this.columnsAvailableFields);
  }

  /***************************/
  /* Report Management      */
  /***************************/

  get reportSearchConfig() {
    // For chart reports, use chart-specific filters and settings
    if (this.currentView === 'chart') {
      const chartConfig = this.chart.toConfig();
      return {
        viewType: this.currentView,
        filters: {
          // Chart queries are based on field selection and time ranges, not table filters
          selectedField: chartConfig.selectedField,
          selectedDataset: chartConfig.selectedDataset,
          selectedTimeRange: chartConfig.selectedTimeRange,
        },
        chartSettings: chartConfig,
      };
    } else {
      // For table reports, use the full filter set
      return {
        viewType: this.currentView,
        filters: {
          programTemplateId: this.args.controller.programTemplateId,
          programCreatedAtStart: this.args.controller.programCreatedAtStart,
          programCreatedAtEnd: this.args.controller.programCreatedAtEnd,
          excludeNonVoucherPrograms: this.args.controller.excludeNonVoucherPrograms,
          courseCashProgramsOnly: this.args.controller.courseCashProgramsOnly,
          applicationStates: this.args.controller.applicationStates?.split(',').filter(Boolean),
          applicationCreatedAtStart: this.args.controller.applicationCreatedAtStart,
          applicationCreatedAtEnd: this.args.controller.applicationCreatedAtEnd,
          paymentStates: this.args.controller.paymentStates?.split(',').filter(Boolean),
          paymentYear: this.args.controller.paymentYear,
          includeCourses: this.args.controller.includeCourses,
          coursesBeginDate: this.args.controller.coursesBeginDate,
          coursesEndDate: this.args.controller.coursesEndDate,
          employeeId: this.args.controller.employeeId,
          terminatedEmployees: this.args.controller.terminatedEmployees,
          excludeTermVerified: this.args.controller.excludeTermVerified,
          approverId: this.args.controller.approverId,
        },
        tableSettings: {
          showIndividualApplications: this.effectiveShowIndividualApplications,
          selectedColumns: this.args.controller.selectedColumns,
          columnOrder: this.columnOrder,
        },
      };
    }
  }

  /** Default table filter values — preserves current page/limit, resets all filters. */
  private get defaultTableFilterState(): Record<string, unknown> {
    return {
      page: this.args.controller.page,
      limit: this.args.controller.limit,
      programTemplateId: undefined,
      programCreatedAtStart: undefined,
      programCreatedAtEnd: undefined,
      excludeNonVoucherPrograms: false,
      showIndividualApplications: false,
      applicationStates: undefined,
      applicationCreatedAtStart: undefined,
      applicationCreatedAtEnd: undefined,
      includePayments: false,
      paymentStates: undefined,
      paymentYear: undefined,
      includeCourses: false,
      coursesBeginDate: undefined,
      coursesEndDate: undefined,
      employeeId: undefined,
      terminatedEmployees: false,
      excludeTermVerified: false,
      approverId: undefined,
      includeInvoiceDate: false,
      courseCashProgramsOnly: false,
    };
  }

  handleLoadReport = (report: ReportModel) => {
    // Set the view based on the report type first
    this.currentView = report.isChartBuilder ? 'chart' : 'table';

    if (report.searchConfig) {
      const config: SearchConfig = report.searchConfig;

      if (this.currentView === 'chart') {
        // Chart values are set by SavedReportsDrawer via chart.setField(), etc.
        // We only need to reset table filters to defaults for chart reports.
        this.args.controller.setProperties(this.defaultTableFilterState);
      } else {
        // For table reports, use the existing table filter loading logic
        // Reset column order to default - will be overridden if saved report has one
        this.columnOrder = [];

        const defaultState = this.defaultTableFilterState;

        const savedValues: Record<string, unknown> = {};

        // Extract saved values from the loaded configuration using object destructuring
        if (config.filters) {
          const filters = config.filters as Record<string, unknown>;

          const programTemplateId = filters.programTemplateId as string | undefined;
          const programCreatedAtStart = filters.programCreatedAtStart as string | undefined;
          const programCreatedAtEnd = filters.programCreatedAtEnd as string | undefined;
          const excludeNonVoucherPrograms = filters.excludeNonVoucherPrograms as
            boolean | undefined;
          const courseCashProgramsOnly = filters.courseCashProgramsOnly as boolean | undefined;
          const applicationStates = filters.applicationStates as string | string[] | undefined;
          const applicationCreatedAtStart = filters.applicationCreatedAtStart as string | undefined;
          const applicationCreatedAtEnd = filters.applicationCreatedAtEnd as string | undefined;
          const paymentStates = filters.paymentStates as string | string[] | undefined;
          const paymentYear = filters.paymentYear as string | undefined;
          const includeCourses = filters.includeCourses as boolean | undefined;
          const coursesBeginDate = filters.coursesBeginDate as string | undefined;
          const coursesEndDate = filters.coursesEndDate as string | undefined;
          const employeeId = filters.employeeId as string | undefined;
          const terminatedEmployees = filters.terminatedEmployees as boolean | undefined;
          const excludeTermVerified = filters.excludeTermVerified as boolean | undefined;
          const approverId = filters.approverId as string | undefined;

          // Build savedValues object with only defined values
          Object.assign(savedValues, {
            ...(programTemplateId !== undefined && { programTemplateId }),
            ...(programCreatedAtStart !== undefined && { programCreatedAtStart }),
            ...(programCreatedAtEnd !== undefined && { programCreatedAtEnd }),
            ...(excludeNonVoucherPrograms !== undefined && { excludeNonVoucherPrograms }),
            ...(courseCashProgramsOnly !== undefined && { courseCashProgramsOnly }),
            ...(applicationStates !== undefined && {
              applicationStates: Array.isArray(applicationStates)
                ? applicationStates.join(',')
                : applicationStates,
            }),
            ...(applicationCreatedAtStart !== undefined && { applicationCreatedAtStart }),
            ...(applicationCreatedAtEnd !== undefined && { applicationCreatedAtEnd }),
            ...(paymentStates !== undefined && {
              paymentStates: Array.isArray(paymentStates) ? paymentStates.join(',') : paymentStates,
            }),
            ...(paymentYear !== undefined && { paymentYear }),
            ...(includeCourses !== undefined && { includeCourses }),
            ...(coursesBeginDate !== undefined && { coursesBeginDate }),
            ...(coursesEndDate !== undefined && { coursesEndDate }),
            ...(employeeId !== undefined && { employeeId }),
            ...(terminatedEmployees !== undefined && { terminatedEmployees }),
            ...(excludeTermVerified !== undefined && { excludeTermVerified }),
            ...(approverId !== undefined && { approverId }),
          });
        }

        // Apply table settings
        if (config.tableSettings?.showIndividualApplications !== undefined) {
          savedValues.showIndividualApplications = config.tableSettings.showIndividualApplications;
        }
        if (config.tableSettings?.selectedColumns) {
          savedValues.selectedColumns = config.tableSettings.selectedColumns;
        }
        if (config.tableSettings?.columnOrder) {
          this.columnOrder = config.tableSettings.columnOrder;
        }

        // Merge default state with saved values (saved values override defaults)
        const finalState = { ...defaultState, ...savedValues };

        // Auto-enable includePayments if paymentStates has values
        if (finalState.paymentStates && String(finalState.paymentStates).trim() !== '') {
          finalState.includePayments = true;
        }

        // Auto-enable includeCourses if course date filters have values
        if (
          (finalState.coursesBeginDate && String(finalState.coursesBeginDate).trim() !== '') ||
          (finalState.coursesEndDate && String(finalState.coursesEndDate).trim() !== '')
        ) {
          finalState.includeCourses = true;
        }

        // Apply the complete state in one operation
        this.args.controller.setProperties(finalState);
      }
    } else {
      // No config - reset to clean state based on view type
      if (this.currentView === 'chart') {
        this.chart.reset();
      }

      // Always reset table filters to default state
      this.args.controller.setProperties(this.defaultTableFilterState);
    }
  };

  handleViewChange = (view: 'table' | 'chart') => {
    this.currentView = view;
    // Clear current report when switching views since this represents a new report type
    this.currentReport = undefined;
  };

  handleDatasetChange = (dataset: string) => {
    this.chart.selectedDataset = dataset;

    // Clear generic filters when switching datasets
    this.genericFilters = {};

    // Reset ALL query params to their controller defaults so they are removed from the URL.
    // Ember only strips a QP from the URL when it matches the default value, so string
    // params that default to `undefined` must be reset to `undefined` (not '').
    // Don't set showIndividualApplications for non-TAS datasets — the
    // effectiveShowIndividualApplications getter already returns true for them,
    // and setting it to true would leak it into the URL (default is false).
    this.args.controller.setProperties({
      dataset,
      page: 1,
      selectedColumns: undefined,
      programTemplateId: undefined,
      programCreatedAtStart: undefined,
      programCreatedAtEnd: undefined,
      excludeNonVoucherPrograms: false,
      showIndividualApplications: false,
      applicationStates: undefined,
      applicationCreatedAtStart: undefined,
      applicationCreatedAtEnd: undefined,
      includePayments: false,
      paymentStates: undefined,
      paymentYear: undefined,
      includeCourses: false,
      coursesBeginDate: undefined,
      coursesEndDate: undefined,
      employeeId: undefined,
      terminatedEmployees: false,
      excludeTermVerified: false,
      approverId: undefined,
      includeInvoiceDate: false,
      courseCashProgramsOnly: false,
      sortField: undefined,
    });
  };

  setCurrentReport = (report: ReportModel) => {
    this.currentReport = report;
    this.markClean();
  };

  /**
   * Quick-save: persist the current report's search config + metadata without opening a dialog.
   * Only works when a report is already loaded (has an id).
   * Preserves existing scope/visibility — just updates searchConfig and metadata.saved_at.
   */
  quickSaveReport = async () => {
    const report = this.currentReport;
    if (!report?.id) return;

    this.isSavingReport = true;
    try {
      const searchConfig = this.reportSearchConfig;
      const serializedQuery = this.args.controller.selectedColumns || '';

      const metadata = {
        ...(report.metadata || {}),
        saved_at: new Date().toISOString(),
        serializedQuery,
      };

      const request = updateReportRequest(report.id, {
        searchConfig,
        metadata,
      });
      request.cacheOptions = { [SkipCache]: true };
      const response = await this.store.request(request);
      const attrs = (response.content as { data: { attributes: Record<string, unknown> } })?.data
        ?.attributes;
      if (attrs) {
        Object.assign(report, attrs);
      }
      this.markClean();
      this.notifications.add('Report saved', { appearance: 'success' });
    } catch (error) {
      console.error('Failed to quick-save report:', error);
      this.notifications.add('Failed to save report', { appearance: 'error' });
    } finally {
      this.isSavingReport = false;
    }
  };

  /**
   * Unpublish the current report — sets scope back to admin_only.
   */
  unpublishReport = async () => {
    const report = this.currentReport;
    if (!report?.id) return;

    this.isSavingReport = true;
    try {
      const metadata = {
        ...(report.metadata || {}),
        scope: 'admin_only' as const,
        publishedAt: undefined,
      };

      const request = updateReportRequest(report.id, { metadata });
      request.cacheOptions = { [SkipCache]: true };
      const response = await this.store.request(request);
      const attrs = (response.content as { data: { attributes: Record<string, unknown> } })?.data
        ?.attributes;
      if (attrs) {
        Object.assign(report, attrs);
      }
      this.notifications.add('Report unpublished', { appearance: 'success' });
    } catch (error) {
      console.error('Failed to unpublish report:', error);
      this.notifications.add('Failed to unpublish report', { appearance: 'error' });
    } finally {
      this.isSavingReport = false;
    }
  };

  openPublishDialog = () => {
    this.showPublishDialog = true;
  };

  closePublishDialog = () => {
    this.showPublishDialog = false;
  };

  handlePublished = (_report: ReportModel) => {
    this.showPublishDialog = false;
    this.notifications.add('Report published', { appearance: 'success' });
  };

  /**
   * Handle column sort from table header clicks.
   * Stores the sort as camelCase field with :asc/:desc suffix.
   * The data provider converts to snake_case at the OpenSearch boundary.
   */
  handleSort = (sortField: string) => {
    this.args.controller.setProperties({
      sortField,
      page: 1,
    });
  };

  get dataProviderComponent() {
    return this.args.dataProviderComponent || ProgramsDataProvider;
  }

  /**
   * Handle column order changes from the table
   * Stores the new order for saving in reports
   */
  handleColumnOrderChange = (newOrder: string[]) => {
    this.columnOrder = newOrder;
  };

  <template>
    <this.dataProviderComponent
      @company={{@company}}
      @selectedDataset={{this.chart.selectedDataset}}
      @programTemplateId={{@controller.programTemplateId}}
      @programCreatedAtStart={{@controller.programCreatedAtStart}}
      @programCreatedAtEnd={{@controller.programCreatedAtEnd}}
      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
      @courseCashProgramsOnly={{@controller.courseCashProgramsOnly}}
      @showIndividualApplications={{this.effectiveShowIndividualApplications}}
      @applicationStates={{@controller.applicationStates}}
      @applicationCreatedAtStart={{@controller.applicationCreatedAtStart}}
      @applicationCreatedAtEnd={{@controller.applicationCreatedAtEnd}}
      @includePayments={{@controller.includePayments}}
      @paymentStates={{@controller.paymentStates}}
      @paymentYear={{@controller.paymentYear}}
      @includeCourses={{@controller.includeCourses}}
      @coursesBeginDate={{@controller.coursesBeginDate}}
      @coursesEndDate={{@controller.coursesEndDate}}
      @employeeId={{@controller.employeeId}}
      @terminatedEmployees={{@controller.terminatedEmployees}}
      @excludeTermVerified={{@controller.excludeTermVerified}}
      @approverId={{@controller.approverId}}
      @includeInvoiceDate={{@controller.includeInvoiceDate}}
      @selectedColumns={{this.selectedColumnsArray}}
      @genericFilters={{this.genericFilters}}
      @isReady={{this.mappingData.isFinished}}
      @programTemplates={{this.programTemplatesData.value}}
      @isLoadingProgramTemplates={{this.programTemplatesData.isLoading}}
      @selectedProgramTemplate={{this.selectedProgramTemplate}}
      @sortField={{@controller.sortField}}
      @page={{@controller.page}}
      @limit={{@controller.limit}}
      @updateQueryParameters={{this.updateQueryParameters}}
      @onResolvedSource={{this.reconcileSelectedColumnsFromResolvedSource}}
      as |provider|
    >
      <StickyLayout
        @sidebarWidth="w-16"
        @sidebarSticky={{true}}
        class={{this.mainContentClasses}}
        {{stickySidebarAdjustment}}
      >
        <:header>
          <Section>
            <:header>
              <div class="tio-row-loose">
                {{yield to="breadcrumbs"}}
                {{#if @showSaveReports}}
                  <ReportStatusBadge
                    @report={{this.currentReport}}
                    @hasUnsavedChanges={{this.hasUnsavedChanges}}
                  />
                {{/if}}
              </div>
            </:header>
            <:actions>
              <div
                class="tio-col-tight w-full items-stretch sm:w-auto sm:flex-row sm:flex-wrap sm:items-center"
              >
                {{#if @showSaveReports}}
                  {{#if this.currentReport}}
                    <Button
                      @icon={{PhFloppyDisk}}
                      @appearance="outlined"
                      @intent="success"
                      @size="sm"
                      @isRunning={{this.isSavingReport}}
                      {{on "click" this.quickSaveReport}}
                      data-test-quick-save-button
                    >
                      Save
                    </Button>
                    {{#if this.currentReport.isUnpublished}}
                      <Button
                        @icon={{PhCloudArrowUp}}
                        @appearance="outlined"
                        @intent="primary"
                        @size="sm"
                        {{on "click" this.openPublishDialog}}
                        data-test-publish-button
                      >
                        Publish
                      </Button>
                    {{else}}
                      <Button
                        @icon={{PhCloudX}}
                        @appearance="outlined"
                        @intent="default"
                        @size="sm"
                        @isRunning={{this.isSavingReport}}
                        {{on "click" this.unpublishReport}}
                        data-test-unpublish-button
                      >
                        Unpublish
                      </Button>
                    {{/if}}
                  {{/if}}
                {{/if}}
                {{yield
                  (hash
                    downloadCsv=provider.downloadCsv
                    currentView=this.currentView
                    onViewChange=this.handleViewChange
                    selectedDataset=this.chart.selectedDataset
                    onDatasetChange=this.handleDatasetChange
                  )
                  to="headerActions"
                }}
              </div>
            </:actions>
          </Section>
        </:header>

        <:main>
          {{#if (eq this.currentView "table")}}
            {{#if provider.isLoading}}
              <div class="tio-row-center p-8">
                <div class="tio-fg-muted">Loading table data...</div>
              </div>
            {{else}}
              <Section class="m-6 min-w-0 overflow-hidden">
                <:body>
                  {{#if this.effectiveShowIndividualApplications}}
                    <ProgramsTable
                      @results={{provider.data}}
                      @isLoading={{provider.isLoading}}
                      @selectedColumns={{this.selectedColumnsArray}}
                      @availableFields={{this.availableFields}}
                      @columnOrder={{this.columnOrder}}
                      @showIndividualApplications={{this.effectiveShowIndividualApplications}}
                      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
                      @courseCashProgramsOnly={{@controller.courseCashProgramsOnly}}
                      @includeCourses={{@controller.includeCourses}}
                      @includeInvoiceDate={{@controller.includeInvoiceDate}}
                      @terminatedEmployees={{@controller.terminatedEmployees}}
                      @excludeTermVerified={{@controller.excludeTermVerified}}
                      @applicationDetailRoute={{@applicationDetailRoute}}
                      @programDetailRoute={{@programDetailRoute}}
                      @programTemplateDetailRoute={{@programTemplateDetailRoute}}
                      @peopleDetailRoute={{@peopleDetailRoute}}
                      @companyDetailRoute={{@companyDetailRoute}}
                      @financialInstitutionDetailRoute={{@financialInstitutionDetailRoute}}
                      @planDetailRoute={{@planDetailRoute}}
                      @documentDetailRoute={{@documentDetailRoute}}
                      @assetDetailRoute={{@assetDetailRoute}}
                      @paymentStates={{@controller.paymentStates}}
                      @sortField={{@controller.sortField}}
                      @onSort={{this.handleSort}}
                      @limit={{@controller.limit}}
                      @page={{@controller.page}}
                      @selectedFilters={{this.selectedFilters}}
                      @onPageChange={{fn (mut @controller.page)}}
                      @onColumnOrderChange={{this.handleColumnOrderChange}}
                    />
                  {{else}}
                    <ProgramsAggregateTable
                      @results={{provider.data}}
                      @isLoading={{provider.isLoading}}
                      @baseRoute={{@baseRoute}}
                      @applicationDetailRoute={{@applicationDetailRoute}}
                      @programDetailRoute={{@programDetailRoute}}
                      @programTemplateDetailRoute={{@programTemplateDetailRoute}}
                      @peopleDetailRoute={{@peopleDetailRoute}}
                      @companyDetailRoute={{@companyDetailRoute}}
                      @financialInstitutionDetailRoute={{@financialInstitutionDetailRoute}}
                      @planDetailRoute={{@planDetailRoute}}
                      @documentDetailRoute={{@documentDetailRoute}}
                      @assetDetailRoute={{@assetDetailRoute}}
                      @limit={{@controller.limit}}
                      @page={{@controller.page}}
                      @selectedColumns={{this.selectedColumnsArray}}
                      @availableFields={{this.availableFields}}
                      @selectedFilters={{this.selectedFilters}}
                      @onPageChange={{fn (mut @controller.page)}}
                    />
                  {{/if}}
                </:body>
              </Section>
            {{/if}}
          {{else}}
            {{! Chart view }}
            <Section class="m-6">
              <:header>
                📊 Chart View -
                {{provider.totalRecords}}
                records ({{provider.executionTime}}ms)
              </:header>
              <:body>
                {{#if provider.isLoading}}
                  <div class="tio-row-center p-8">
                    <div class="tio-fg-muted">Loading chart data...</div>
                  </div>
                {{else}}
                  <ChartView
                    @selectedField={{this.chart.selectedField}}
                    @chartType={{this.chart.selectedType}}
                    @dateGrouping={{this.chart.dateGrouping}}
                    @showDataTable={{this.chart.showDataTable}}
                    @selectedDataset={{this.chart.selectedDataset}}
                    @selectedTimeRange={{this.chart.selectedTimeRange}}
                    @summaryTitle={{this.chart.summaryTitle}}
                    @sortOrder={{this.chart.sortOrder}}
                    @companyId={{this.companyId}}
                    @showIndividualApplications={{this.effectiveShowIndividualApplications}}
                    @onFieldChange={{this.chart.setField}}
                    @onChartTypeChange={{this.chart.setType}}
                    @onDateGroupingChange={{this.chart.setDateGrouping}}
                    @onShowDataTableChange={{this.chart.setShowDataTable}}
                    @onDatasetChange={{this.chart.setDataset}}
                    @onTimeRangeChange={{this.chart.setTimeRange}}
                    @onSummaryTitleChange={{this.chart.setSummaryTitle}}
                    @onSortOrderChange={{this.chart.setSortOrder}}
                  />
                {{/if}}
              </:body>
            </Section>
          {{/if}}
        </:main>

        <:sidebar>
          <TooltipButton
            @tooltip="Advanced Filters"
            @tooltipPlacement="left"
            @appearance="minimal"
            @intent="default"
            @size="sm"
            class="!p-1 !rounded-md"
            @tooltipDisabled={{this.isDrawerOpen}}
            data-tour="advanced-filters-button"
            {{on "click" (fn this.openDrawer "advanced-filters")}}
          >
            <span class="relative inline-flex">
              <PhFunnel @size={{24}} class="tio-fg-secondary" />
              {{#if this.hasActiveAdvancedFilters}}
                <span
                  class="absolute w-3 h-3 tio-bg-danger rounded-full border-2 tio-border-surface -top-1 -right-1"
                  aria-label="Active filters"
                ></span>
              {{/if}}
            </span>
          </TooltipButton>

          {{#if @showSaveReports}}
            <TooltipButton
              @tooltip="Load & Save Report"
              @tooltipPlacement="left"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              @icon={{PhFloppyDisk}}
              @iconSize={{24}}
              class="!p-1 !rounded-md"
              @tooltipDisabled={{this.isDrawerOpen}}
              {{on "click" (fn this.openDrawer "load-save")}}
            />
          {{/if}}

          <TooltipButton
            @tooltip="Download CSV"
            @tooltipPlacement="left"
            @appearance="minimal"
            @intent="default"
            @size="sm"
            @icon={{PhTrayArrowDown}}
            @iconSize={{24}}
            class="!p-1 !rounded-md"
            @tooltipDisabled={{this.isDrawerOpen}}
            {{on "click" provider.downloadCsv}}
          />
        </:sidebar>
      </StickyLayout>

      {{! Drawer for Advanced Filters and Load/Save }}
      <div class="drawer-container {{if this.isDrawerOpen 'drawer-open' 'drawer-closed'}}">
        <Drawer
          @isOpen={{this.isDrawerOpen}}
          @onClose={{this.closeDrawer}}
          @placement="right"
          @size="lg"
          @closeOnOutsideClick={{this.drawerCloseOnOutsideClick}}
        >
          <:header>
            {{#if (eq this.selectedDrawerType "advanced-filters")}}
              Advanced Options
            {{else if (eq this.selectedDrawerType "load-save")}}
              Load & Save Report
            {{/if}}
          </:header>
          <:body>
            {{#if (eq this.selectedDrawerType "advanced-filters")}}
              {{#if @showColumnSelector}}
                <div class="border-b tio-border-default mb-4">
                  <nav class="-mb-px flex gap-8 px-4" aria-label="Tabs">
                    <button
                      type="button"
                      class="{{if
                          (eq this.activeFilterTab 'filters')
                          'tio-border-info tio-fg-info'
                          'border-transparent tio-fg-muted hover:tio-fg-secondary hover:tio-border-default'
                        }}
                        whitespace-nowrap py-3 px-2 border-b-2 tio-text-sm-medium"
                      {{on "click" this.selectFiltersTab}}
                    >
                      Filters
                    </button>
                    <button
                      type="button"
                      class="{{if
                          (eq this.activeFilterTab 'columns')
                          'tio-border-info tio-fg-info'
                          'border-transparent tio-fg-muted hover:tio-fg-secondary hover:tio-border-default'
                        }}
                        whitespace-nowrap py-3 px-2 border-b-2 tio-text-sm-medium"
                      {{on "click" this.selectColumnsTab}}
                    >
                      Columns
                    </button>
                  </nav>
                </div>
              {{/if}}

              {{#if (eq this.activeFilterTab "filters")}}
                <div class="{{if @showColumnSelector 'p-4' 'pt-0 p-4'}}">
                  {{#if this.isTasDataset}}
                    <AdvancedProgramFilters
                      @company={{@company}}
                      @isAdminContext={{@isAdminContext}}
                      @isCompanyLocked={{this.isCompanyLocked}}
                      @onCompanyChange={{@onCompanyChange}}
                      @availableFieldKeys={{this.availableFieldKeys}}
                      @programTemplateId={{@controller.programTemplateId}}
                      @programCreatedAtStart={{@controller.programCreatedAtStart}}
                      @programCreatedAtEnd={{@controller.programCreatedAtEnd}}
                      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
                      @courseCashProgramsOnly={{@controller.courseCashProgramsOnly}}
                      @showIndividualApplications={{this.effectiveShowIndividualApplications}}
                      @applicationStates={{@controller.applicationStates}}
                      @applicationCreatedAtStart={{@controller.applicationCreatedAtStart}}
                      @applicationCreatedAtEnd={{@controller.applicationCreatedAtEnd}}
                      @applicationDateRangePreset={{@controller.applicationDateRangePreset}}
                      @includePayments={{@controller.includePayments}}
                      @paymentStates={{@controller.paymentStates}}
                      @paymentYear={{@controller.paymentYear}}
                      @includeCourses={{@controller.includeCourses}}
                      @coursesBeginDate={{@controller.coursesBeginDate}}
                      @coursesEndDate={{@controller.coursesEndDate}}
                      @employeeId={{@controller.employeeId}}
                      @terminatedEmployees={{@controller.terminatedEmployees}}
                      @excludeTermVerified={{@controller.excludeTermVerified}}
                      @approverId={{@controller.approverId}}
                      @includeInvoiceDate={{@controller.includeInvoiceDate}}
                      @onFilterUpdate={{this.updateQueryParameters}}
                    />
                  {{else}}
                    <div class="tio-col-extra-loose">
                      {{#if this.shouldShowNonTasPartnerFilter}}
                        <fieldset>
                          <div class="tio-col">
                            <h3
                              class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted"
                            >Partner</h3>
                            <ReSelect
                              @label="Filter by Company Name"
                              @items={{this.partnerCompanyItems}}
                              @selected={{this.companyId}}
                              @onChange={{this.partnerCompanyDidChange}}
                              @searchable={{true}}
                              @clearable={{true}}
                              @placeholder="All Partners"
                              @size="sm"
                              @isLoading={{this.partnerCompaniesData.isLoading}}
                              @disabled={{this.isCompanyLocked}}
                            />
                            {{#if this.isCompanyLocked}}
                              <p class="text-xs tio-fg-muted italic">
                                Unpublish to change company
                              </p>
                            {{/if}}
                          </div>
                        </fieldset>
                      {{/if}}

                      <DatasetFilters
                        @fields={{this.availableFilterFields}}
                        @filters={{this.genericFilters}}
                        @onFilterUpdate={{this.updateGenericFilters}}
                        @onClearFilters={{this.clearGenericFilters}}
                        @isLoading={{this.mappingData.isLoading}}
                      />
                    </div>
                  {{/if}}
                </div>
              {{else if (and @showColumnSelector (eq this.activeFilterTab "columns"))}}
                <div class="p-4">
                  <ColumnSelector
                    @groupedFields={{this.groupedFields}}
                    @isColumnSelected={{this.isColumnSelected}}
                    @handleColumnToggle={{this.handleColumnToggle}}
                    @selectAllColumns={{this.selectAllColumns}}
                    @deselectAllColumns={{this.deselectAllColumns}}
                    @isLoading={{this.mappingData.isLoading}}
                    @totalFieldCount={{this.columnsAvailableFields.length}}
                    @selectedCount={{this.selectedColumnsArray.length}}
                  />
                </div>
              {{/if}}
            {{else if (eq this.selectedDrawerType "load-save")}}
              <SavedReportsDrawer
                @onLoad={{this.handleLoadReport}}
                @onExport={{provider.downloadCsv}}
                @searchData={{this.reportSearchConfig}}
                @reportType={{if (eq this.currentView "chart") "chart_builder" "table_builder"}}
                @currentReport={{this.currentReport}}
                @showPartnerVisibility={{@isGlobalContext}}
                @updateQueryParameters={{this.updateQueryParameters}}
                @setCurrentReport={{this.setCurrentReport}}
                @setDataset={{this.chart.setDataset}}
                @setGroupByField={{this.chart.setField}}
                @setChartType={{this.chart.setType}}
                @setTimeRange={{this.chart.setTimeRange}}
                @setDateGrouping={{this.chart.setDateGrouping}}
                @setShowTableView={{this.chart.setShowDataTable}}
              />
            {{/if}}
          </:body>
        </Drawer>
      </div>

      {{! Column Removal Warning Modal }}
      <Modal
        @isOpen={{this.showColumnWarningModal}}
        @onClose={{this.cancelColumnRemoval}}
        @size="sm"
      >
        <:header>Cannot Remove Column</:header>
        <:body>
          <p class="text-sm tio-fg-secondary mb-4">
            This column is required by the following active filter{{if
              (gt this.affectedFilters.length 1)
              "s"
            }}:
          </p>
          <ul class="list-disc list-inside space-y-1 mb-4">
            {{#each this.affectedFilters as |filterKey|}}
              <li class="tio-text-sm-medium tio-fg-primary">
                {{filterKey}}
              </li>
            {{/each}}
          </ul>
          <p class="text-sm tio-fg-secondary">
            Removing this column will also clear the associated filter{{if
              (gt this.affectedFilters.length 1)
              "s"
            }}. Do you want to proceed?
          </p>
        </:body>
        <:footer>
          <Footer
            @submitText="Remove Column"
            @onSubmit={{this.confirmColumnRemoval}}
            @onCancel={{this.cancelColumnRemoval}}
            @cancelText="Cancel"
            @submitIntent="danger"
          />
        </:footer>
      </Modal>

      {{! Publish Report Dialog }}
      {{#if this.currentReport}}
        <PublishReportDialog
          @isOpen={{this.showPublishDialog}}
          @onClose={{this.closePublishDialog}}
          @onPublish={{this.handlePublished}}
          @report={{this.currentReport}}
          @currentCompanyId={{@company.id}}
        />
      {{/if}}
    </this.dataProviderComponent>
  </template>
}
